import { useTranslation } from "react-i18next";
import { Text, Stack } from "@certn/cascadia";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  DataDisplay,
  GhostButton,
  LinkButton,
  Loader,
  Select,
  SelectItem,
  StatusTag,
} from "@certn/cascadia-react";
import { useState } from "react";
import { CCRCEditModal } from "./CCRCEditModal";
import { useFormikContext } from "formik";
import { AdjudicationStatusTag } from "../../../../utils/renderStatusTag";
import type {
  CodeOption,
  SubCategory,
  ModalSaveData,
  Code,
} from "../../types/canadian-criminal-record-types";
import type { AdjudicationFormValues } from "../../types/form-values";
import {
  getCriteriaAndCategory,
  getExistingCodeData,
  getCodeScore,
} from "../../utils";

interface CCRCAdjudicationCodesProps {
  subCategory: SubCategory;
}

export function CCRCAdjudicationCodes({
  subCategory,
}: CCRCAdjudicationCodesProps) {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CodeOption | null>(null);
  const [selectKey, setSelectKey] = useState(0);
  const { data: codes, isLoading: isCodesLoading } = useClientPortalAPI(
    `/api/internal/client-portal/adjudication-rubrics/subcategory-codes/`,
    {
      subcategory: subCategory.value,
    }
  );
  const { values, setFieldValue } = useFormikContext<AdjudicationFormValues>();

  // Helper function to create a reset code structure with all values set to NOT_SET
  const createCodeStructure = (code: Code, outcome: string) => ({
    ...code,
    global_outcome: outcome,
    one_offense: {
      zero_to_three_years_since_offense_outcome: "NOT_SET",
      three_to_ten_years_since_offense_outcome: "NOT_SET",
      ten_or_more_years_since_offense_outcome: "NOT_SET",
    },
    two_or_more_offenses: {
      zero_to_three_years_since_offense_outcome: "NOT_SET",
      three_to_ten_years_since_offense_outcome: "NOT_SET",
      ten_or_more_years_since_offense_outcome: "NOT_SET",
    },
  });

  const handleSave = (code: CodeOption, data: ModalSaveData): void => {
    const { pickerSelections, globalOutcome, selectedOutcome } = data;

    const { criteriaIndex, categoryIndex, currentRuleset } =
      getCriteriaAndCategory(values, subCategory);

    if (criteriaIndex === -1 || categoryIndex === -1) {
      throw new Error(
        `Criteria or category not found for subcategory: ${subCategory.value}`
      );
    }

    // Find the code in the category's codes array
    const category = currentRuleset[categoryIndex];
    const codes = [...(category.codes || [])];
    const existingCode = codes.find((c) => c.code === code.key);

    if (!existingCode) {
      throw new Error(`Code not found: ${code.key}`);
    }

    const codeIndex = codes.findIndex((c) => c.code === code.key);

    // Create updated code object
    const updatedCode = {
      ...codes[codeIndex],
      // Set global_outcome if GLOBAL mode is selected, otherwise keep it as NOT_SET
      global_outcome:
        selectedOutcome === "GLOBAL" && globalOutcome
          ? globalOutcome
          : "NOT_SET",
      // Only update conditional values if CONDITIONAL mode is selected
      one_offense: {
        zero_to_three_years_since_offense_outcome:
          selectedOutcome === "CONDITIONAL"
            ? pickerSelections["1 offence-0_3_years"] || "NOT_SET"
            : "NOT_SET",
        three_to_ten_years_since_offense_outcome:
          selectedOutcome === "CONDITIONAL"
            ? pickerSelections["1 offence-3_10_years"] || "NOT_SET"
            : "NOT_SET",
        ten_or_more_years_since_offense_outcome:
          selectedOutcome === "CONDITIONAL"
            ? pickerSelections["1 offence-10_plus_years"] || "NOT_SET"
            : "NOT_SET",
      },
      two_or_more_offenses: {
        zero_to_three_years_since_offense_outcome:
          selectedOutcome === "CONDITIONAL"
            ? pickerSelections["2+ offences-0_3_years"] || "NOT_SET"
            : "NOT_SET",
        three_to_ten_years_since_offense_outcome:
          selectedOutcome === "CONDITIONAL"
            ? pickerSelections["2+ offences-3_10_years"] || "NOT_SET"
            : "NOT_SET",
        ten_or_more_years_since_offense_outcome:
          selectedOutcome === "CONDITIONAL"
            ? pickerSelections["2+ offences-10_plus_years"] || "NOT_SET"
            : "NOT_SET",
      },
    };

    // Create new codes array with updated code
    const updatedCodes = [
      ...codes.slice(0, codeIndex),
      updatedCode,
      ...codes.slice(codeIndex + 1),
    ];

    // Create new ruleset with updated codes
    const updatedRuleset = [
      ...currentRuleset.slice(0, categoryIndex),
      {
        ...currentRuleset[categoryIndex],
        codes: updatedCodes,
      },
      ...currentRuleset.slice(categoryIndex + 1),
    ];

    // Update the form values with new reference
    setFieldValue(`criteria[${criteriaIndex}].ruleset`, updatedRuleset);
    setFieldValue(`criteria[${criteriaIndex}].set`, true);
    setFieldValue(
      `criteria[${criteriaIndex}].ruleset[${categoryIndex}].set`,
      true
    );
  };

  const handleBulkSetGlobalOutcome = (outcome: string): void => {
    const { criteriaIndex, categoryIndex, currentRuleset } =
      getCriteriaAndCategory(values, subCategory);
    if (criteriaIndex === -1 || categoryIndex === -1) {
      throw new Error(
        `Criteria or category not found for subcategory: ${subCategory.value}`
      );
    }

    const category = currentRuleset[categoryIndex];
    const existingCodes = category.codes || [];

    // Update all codes with the selected global outcome
    const updatedCodes = existingCodes.map((code) =>
      createCodeStructure(code, outcome)
    );
    // Create new ruleset with updated codes
    const updatedRuleset = [
      ...currentRuleset.slice(0, categoryIndex),
      {
        ...currentRuleset[categoryIndex],
        codes: updatedCodes,
      },
      ...currentRuleset.slice(categoryIndex + 1),
    ];

    // Update the form values
    setFieldValue(`criteria[${criteriaIndex}].ruleset`, updatedRuleset);
    setFieldValue(`criteria[${criteriaIndex}].set`, true);
    setFieldValue(
      `criteria[${criteriaIndex}].ruleset[${categoryIndex}].set`,
      true
    );
  };

  const handleResetAllCodes = (): void => {
    const { criteriaIndex, categoryIndex, currentRuleset } =
      getCriteriaAndCategory(values, subCategory);

    if (criteriaIndex === -1 || categoryIndex === -1) {
      throw new Error(
        `Criteria or category not found for subcategory: ${subCategory.value}`
      );
    }

    const category = currentRuleset[categoryIndex];
    const existingCodes = category.codes || [];

    // Reset all codes to NOT_SET
    const updatedCodes = existingCodes.map((code) =>
      createCodeStructure(code, "NOT_SET")
    );

    // Create new ruleset with updated codes
    const updatedRuleset = [
      ...currentRuleset.slice(0, categoryIndex),
      {
        ...currentRuleset[categoryIndex],
        codes: updatedCodes,
      },
      ...currentRuleset.slice(categoryIndex + 1),
    ];

    // Update the form values
    setFieldValue(`criteria[${criteriaIndex}].ruleset`, updatedRuleset);

    // Force Select to reset by changing its key
    setSelectKey((prev) => prev + 1);
  };

  // Create a key based on the criteria ruleset to force re-render when it changes
  const { categoryData } = getCriteriaAndCategory(values, subCategory);
  const dataDisplayKey = JSON.stringify(categoryData?.codes || []);

  return (
    <>
      {isCodesLoading ? (
        <Loader />
      ) : (
        <Stack gap="space-1">
          <Text style="global/title-small">{subCategory.label}</Text>
          <br />
          <Stack
            orientation="horizontal"
            justify="space-between"
            align="center"
          >
            <Text style="global/heading-medium">
              {t("Set all offences to")}
            </Text>
            <Stack orientation="horizontal" gap="space-1">
              <GhostButton
                onPress={handleResetAllCodes}
                testID="reset-all-codes-button"
              >
                {t("Reset")}
              </GhostButton>
              <Select
                key={selectKey}
                testID="bulk-set-outcome-select"
                placeholder={t("Select outcome")}
                onSelectionChange={(key) => {
                  if (key) {
                    handleBulkSetGlobalOutcome(key as string);
                  }
                }}
              >
                <SelectItem id="REVIEW">{t("Review")}</SelectItem>
                <SelectItem id="CLEAR">{t("Clear")}</SelectItem>
                <SelectItem id="CRITERIA_NOT_MET">
                  {t("Does not meet")}
                </SelectItem>
              </Select>
            </Stack>
          </Stack>
          <DataDisplay
            key={dataDisplayKey}
            rows={codes || []}
            columns={[
              {
                key: "code_label",
                name: t("Crim. Code"),
                isRowHeader: true,
                render: ({ code_label }) => (
                  <StatusTag
                    statusInfoIcon={false}
                    fill="background-accent-neutral-weak"
                    color="text-base"
                  >
                    {code_label}
                  </StatusTag>
                ),
              },
              {
                key: "offences",
                name: t("Offences"),
                isRowHeader: true,
                render: ({ label }) => (
                  <Text style="input/helper">{label}</Text>
                ),
              },
              {
                key: "rules",
                name: t("Rules"),
                isRowHeader: true,
                render: (row) =>
                  AdjudicationStatusTag(getCodeScore(values, subCategory, row)),
              },
              {
                key: "edit",
                name: "",
                isRowHeader: true,
                render: (row) => (
                  <LinkButton
                    onPress={() => {
                      setIsEditModalOpen(true);
                      setSelectedCode(row);
                    }}
                    linkStyle="monochrome"
                    testID="edit-button"
                  >
                    {t("Edit")}
                  </LinkButton>
                ),
              },
            ]}
          />
        </Stack>
      )}

      {isEditModalOpen && selectedCode && (
        <CCRCEditModal
          isOpen={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          code={selectedCode}
          existingCodeData={getExistingCodeData(
            values,
            subCategory,
            selectedCode
          )}
          handleSave={(data) => handleSave(selectedCode, data)}
        />
      )}
    </>
  );
}
